import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight, Truck, ShieldCheck, ShieldX, Clock, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";

const STATUS_META = {
  pending_payment:                    { label: "Pending Payment", cls: "bg-slate-100 text-slate-800 border-slate-200", Icon: Clock },
  authentication_in_progress:         { label: "Authentication In Progress", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Loader2 },
  photo_matched_creating_documents:   { label: "Photo Matched · Creating Documents", cls: "bg-green-100 text-green-800 border-green-200", Icon: ShieldCheck },
  authenticated_creating_documents:   { label: "Authenticated · Creating Documents", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: ShieldCheck },
  shipped:                            { label: "Shipped", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Truck },
  did_not_photo_match:                { label: "Item Did Not Photo Match", cls: "bg-red-100 text-red-800 border-red-200", Icon: ShieldX },
};

export default function SubmissionCenter() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const renderStatus = (status) => {
    const meta = STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-800", Icon: Package };
    const Icon = meta.Icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono-rgu uppercase tracking-widest border ${meta.cls}`}>
        <Icon className="w-3.5 h-3.5" /> {meta.label}
      </span>
    );
  };

  return (
    <div className="App">
      <Header />
      <div className="rgu-container py-12" data-testid="submission-center">
        <div className="rgu-eyebrow text-slate-500 mb-3">Account · Submission Center</div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0A192F]">Your Submissions</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Track every item&apos;s authentication progress. Receive Letter of Opinion offers for items that did not photo match.
        </p>

        {loading ? (
          <div className="mt-10 text-slate-500">Loading…</div>
        ) : subs.length === 0 ? (
          <div className="mt-10 border border-slate-200 p-12 text-center">
            <Package className="w-10 h-10 mx-auto text-slate-400" />
            <h3 className="font-display text-xl mt-4 text-[#0A192F]">No submissions yet</h3>
            <p className="text-slate-500 mt-2">Begin a new authentication submission to get started.</p>
            <Link to="/submit/photo_match" className="rgu-btn-primary inline-flex mt-6 text-xs uppercase tracking-widest" data-testid="start-submission-link">
              Start a Photo Match Submission <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 space-y-6">
            {subs.map((s) => (
              <div key={s.id} className="border border-slate-200 bg-white" data-testid={`submission-${s.id}`}>
                <div className="px-6 py-5 border-b border-slate-200 flex flex-wrap items-center gap-4 justify-between">
                  <div className="flex items-center gap-4">
                    <div className="font-mono-rgu text-xs uppercase tracking-widest text-slate-500">
                      {s.is_follow_up ? "LOO Follow-up" : (s.auth_type === "photo_match" ? "Photo Match" : "Traditional")} · #{s.id.slice(0, 8).toUpperCase()}
                    </div>
                    {renderStatus(s.status)}
                  </div>
                  <div className="font-mono-rgu text-xs text-slate-500">
                    {new Date(s.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <div className="rgu-eyebrow text-slate-500 mb-3">Items ({s.items.length})</div>
                    <ul className="space-y-2">
                      {s.items.map((it, idx) => (
                        <li key={idx} className="flex items-start justify-between text-sm">
                          <div className="flex-1">
                            <div className="text-[#0A192F]">{it.item_title}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{it.item_description?.slice(0, 80)}</div>
                          </div>
                          <div className="font-mono-rgu text-xs text-slate-500 ml-3">
                            {(STATUS_META[it.status]?.label || it.status).toUpperCase()}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="rgu-eyebrow text-slate-500 mb-3">Order</div>
                    <div className="font-mono-rgu text-sm space-y-1.5">
                      <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>${s.quote.subtotal.toFixed(2)}</span></div>
                      {s.quote.discount_amount > 0 && (
                        <div className="flex justify-between text-green-700"><span>Discount</span><span>− ${s.quote.discount_amount.toFixed(2)}</span></div>
                      )}
                      <div className="flex justify-between text-slate-600"><span>Shipping</span><span>${s.quote.shipping_cost.toFixed(2)}</span></div>
                      <div className="flex justify-between text-[#0A192F] pt-1.5 border-t border-slate-200 mt-1">
                        <span className="font-display">Total</span><span>${s.quote.total.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-2 uppercase tracking-widest">
                        Payment: <span className={s.payment_status === "paid" ? "text-green-700" : "text-amber-700"}>{s.payment_status}</span>
                      </div>
                    </div>

                    {s.status === "shipped" && s.tracking_number && (
                      <div className="mt-4 text-sm">
                        <div className="rgu-eyebrow text-slate-500 mb-1">Tracking</div>
                        <div className="font-mono-rgu">{s.tracking_carrier} · {s.tracking_number}</div>
                      </div>
                    )}

                    {s.payment_status !== "paid" && (
                      <Link to={`/checkout/${s.id}`} className="rgu-btn-primary mt-4 text-xs uppercase tracking-widest justify-center w-full" data-testid={`checkout-${s.id}`}>
                        Complete Checkout <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>

                {s.is_follow_up && (
                  <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-900 font-mono-rgu uppercase tracking-widest flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> Letter of Opinion offer linked to submission #{s.parent_submission_id?.slice(0, 8).toUpperCase()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
